import { useState } from "react";
import { motion } from "framer-motion";
import { MagnifyingGlass, Gear, Rocket } from "@phosphor-icons/react";
import { SERVICES } from "@/constants/testIds";

const phases = [
  {
    testId: SERVICES.revealCard,
    phase: "R1",
    name: "Reveal",
    subtitle: "See what's really happening",
    icon: MagnifyingGlass,
    problem: "Most HR teams know something isn't working. They don't know exactly where the time goes, or how much it costs. We measure it.",
    what: [
      "Map every step in your key HR processes — end to end",
      "Measure how long each step takes and who's doing it",
      "Find the tasks that are manual, duplicated or unnecessary",
      "Show you exactly where the function is overstretched or understaffed",
    ],
    youGet: [
      "A clear picture of how your HR actually operates today",
      "The real cost of each process, backed by data",
      "A prioritised list of what to fix first",
    ],
  },
  {
    testId: SERVICES.rewireCard,
    phase: "R2",
    name: "Rewire",
    subtitle: "Build the capability to change",
    icon: Gear,
    problem: "New tools fail when the team isn't ready. We prepare your people and your processes before anything changes.",
    what: [
      "Run workshops on how to think about and improve processes",
      "Train your team to use the tools that will replace manual work",
      "Redesign how managers measure success — outcomes, not hours",
    ],
    youGet: [
      "A team that understands why processes are changing",
      "Practical skills your people can use immediately",
      "New performance measures that reflect how work actually flows",
    ],
  },
  {
    testId: SERVICES.rebuildCard,
    phase: "R3",
    name: "Rebuild",
    subtitle: "Redesign and implement",
    icon: Rocket,
    problem: "This is where the change happens. We redesign your processes, connect your systems, and restructure roles so the function runs the way it should.",
    what: [
      "Set up automations that replace repetitive manual tasks",
      "Connect systems that currently don't talk to each other",
      "Redesign roles and responsibilities to match the new way of working",
    ],
    youGet: [
      "Processes that run faster with fewer handoffs",
      "Systems that share data instead of creating duplicate work",
      "An updated team structure with clear, rewritten job descriptions",
    ],
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.15 },
  }),
};

export default function Services() {
  const [expanded, setExpanded] = useState(null);

  return (
    <section
      id="services"
      data-testid={SERVICES.section}
      className="relative py-24 sm:py-32 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <motion.p
          data-testid={SERVICES.overline}
          className="overline mb-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          How we work
        </motion.p>
        <motion.h2
          data-testid={SERVICES.heading}
          className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 text-themed-primary"
          style={{ fontFamily: "var(--sv-heading-font)" }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Three steps. <span className="metallic-text">No jargon.</span>
        </motion.h2>
        <motion.p
          className="text-themed-body text-base sm:text-lg max-w-xl mb-16 leading-relaxed"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          We look at how your people function works, find what's not working, and fix it. Each step builds on the last.
        </motion.p>

        <div className="space-y-5">
          {phases.map((phase, i) => {
            const isOpen = expanded === i;
            return (
              <motion.div
                key={phase.name}
                data-testid={phase.testId}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="glass-card overflow-hidden cursor-pointer"
                onClick={() => setExpanded(isOpen ? null : i)}
              >
                <div className="p-6 sm:p-8 flex items-start gap-5">
                  <phase.icon
                    size={24}
                    weight="regular"
                    className="flex-shrink-0 mt-1"
                    style={{ color: "var(--sv-gold)" }}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <span
                          className="text-xs tracking-widest uppercase text-themed-gold"
                          style={{ fontFamily: "var(--sv-mono-font)" }}
                        >
                          {phase.phase}
                        </span>
                        <h3
                          className="text-xl sm:text-2xl font-semibold text-themed-primary"
                          style={{ fontFamily: "var(--sv-heading-font)" }}
                        >
                          {phase.name}
                        </h3>
                        <p
                          className="text-sm mt-1"
                          style={{ color: "var(--sv-text-muted)", fontFamily: "var(--sv-mono-font)" }}
                        >
                          {phase.subtitle}
                        </p>
                      </div>
                      <span
                        className="text-themed-muted text-lg transition-transform duration-300 flex-shrink-0"
                        style={{ transform: isOpen ? "rotate(45deg)" : "rotate(0)" }}
                      >
                        +
                      </span>
                    </div>
                    <p className="text-themed-body text-sm leading-relaxed mt-4">
                      {phase.problem}
                    </p>
                  </div>
                </div>

                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.3 }}
                    className="px-6 sm:px-8 pb-8"
                  >
                    <div
                      className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 ml-0 sm:ml-11"
                      style={{ borderTop: "1px solid var(--sv-border)" }}
                    >
                      <div>
                        <h4
                          className="text-xs tracking-widest uppercase mb-4"
                          style={{ fontFamily: "var(--sv-mono-font)", color: "var(--sv-gold)" }}
                        >
                          What we do
                        </h4>
                        <ul className="space-y-3">
                          {phase.what.map((item) => (
                            <li key={item} className="flex items-start gap-2.5 text-sm text-themed-secondary leading-relaxed">
                              <span className="accent-dot mt-1.5" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4
                          className="text-xs tracking-widest uppercase mb-4"
                          style={{ fontFamily: "var(--sv-mono-font)", color: "var(--sv-gold)" }}
                        >
                          What you get
                        </h4>
                        <ul className="space-y-3">
                          {phase.youGet.map((item) => (
                            <li key={item} className="flex items-start gap-2.5 text-sm text-themed-secondary leading-relaxed">
                              <span className="accent-dot mt-1.5" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
